import { useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { clearSession, getUser, hasPermission, isPlatformAdmin, userDisplayName, userDisplayRole, userScopeLabel, type AuthUser } from "../../domains/auth/session";

export type NavItem = {
  to: string;
  label: string;
  roles?: string[];
  /** Any-of: user must hold at least one of these permissions to see the item. */
  permissions?: string[];
  end?: boolean;
};

export type NavGroup = {
  id: string;
  label: string;
  items: NavItem[];
};

const NAV_FOLD_KEY = "pg_nav_folds";
const SIDEBAR_COLLAPSE_KEY = "pg_sidebar_collapsed";
const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

function itemVisible(item: NavItem, role: string | undefined, user?: AuthUser | null): boolean {
  if (item.roles && (role == null || !item.roles.includes(role))) return false;
  if (item.permissions && item.permissions.length > 0) {
    if (!item.permissions.some((p) => hasPermission(user, p))) return false;
  }
  return true;
}

function pathMatches(pathname: string, item: NavItem): boolean {
  if (item.end || item.to === "/") return pathname === item.to || pathname === "";
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function readFolds(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(NAV_FOLD_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeFolds(folds: Record<string, boolean>) {
  localStorage.setItem(NAV_FOLD_KEY, JSON.stringify(folds));
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function navIcon(to: string): ReactNode {
  if (to === "/" || to === "/dashboard") return <DashboardOutlinedIcon fontSize="small" />;
  if (to.startsWith("/account")) return <LockOutlinedIcon fontSize="small" />;
  if (to.startsWith("/field/leads") || to.startsWith("/kyc")) return <AssignmentOutlinedIcon fontSize="small" />;
  if (to.startsWith("/field/installations") || to.startsWith("/installations") || to.startsWith("/key-approvals") || to.startsWith("/openpaygo-exceptions")) {
    return <BuildOutlinedIcon fontSize="small" />;
  }
  if (
    to.startsWith("/field/payments") ||
    to.startsWith("/payment") ||
    to.startsWith("/reconciliation") ||
    to.startsWith("/commissions") ||
    to.startsWith("/overdue")
  ) {
    return <PaymentsOutlinedIcon fontSize="small" />;
  }
  if (to.startsWith("/customers") || to.startsWith("/vc") || to.startsWith("/portal")) return <PeopleOutlinedIcon fontSize="small" />;
  if (to.startsWith("/devices") || to.startsWith("/manufacturers")) return <DevicesOutlinedIcon fontSize="small" />;
  if (to.startsWith("/map")) return <MapOutlinedIcon fontSize="small" />;
  if (to.startsWith("/analytics") || to.startsWith("/dmrv") || to.startsWith("/billing")) return <AnalyticsOutlinedIcon fontSize="small" />;
  if (to.startsWith("/bulk") || to.startsWith("/products")) return <Inventory2OutlinedIcon fontSize="small" />;
  if (to.startsWith("/sms") || to.startsWith("/notifications")) return <SmsOutlinedIcon fontSize="small" />;
  if (
    to.startsWith("/admin") ||
    to.startsWith("/users") ||
    to.startsWith("/tenants") ||
    to.startsWith("/security") ||
    to.startsWith("/areas") ||
    to.startsWith("/discounts") ||
    to.startsWith("/audit")
  ) {
    return <SettingsOutlinedIcon fontSize="small" />;
  }
  return <HomeOutlinedIcon fontSize="small" />;
}

/**
 * Admin AppShell — MUI Drawer (temporary on mobile, permanent mini-collapse on desktop).
 */
export function AppShell({ nav }: { nav: NavGroup[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = getUser();
  const role = user?.role;
  const [folds, setFolds] = useState<Record<string, boolean>>(readFolds);
  const [collapsed, setCollapsed] = useState<boolean>(readSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleGroups = useMemo(
    () =>
      nav
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => itemVisible(item, role, user)),
        }))
        .filter((group) => group.items.length > 0),
    // user.permissions changes after /auth/me enrichment; role changes trigger re-read
    [nav, role, user],
  );

  useEffect(() => {
    const active = visibleGroups.find((g) => g.items.some((item) => pathMatches(location.pathname, item)));
    if (!active) return;
    setFolds((prev) => {
      if (prev[active.id] !== false) return prev;
      const next = { ...prev, [active.id]: true };
      writeFolds(next);
      return next;
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  function isOpen(groupId: string): boolean {
    if (!isMobile && collapsed) return false;
    if (groupId in folds) return folds[groupId] !== false;
    const group = visibleGroups.find((g) => g.id === groupId);
    if (!group) return true;
    return group.items.some((item) => pathMatches(location.pathname, item));
  }

  function toggleGroup(groupId: string) {
    if (!isMobile && collapsed) {
      setCollapsed(false);
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, "0");
      setFolds((prev) => {
        const next = { ...prev, [groupId]: true };
        writeFolds(next);
        return next;
      });
      return;
    }
    setFolds((prev) => {
      const next = { ...prev, [groupId]: !isOpen(groupId) };
      writeFolds(next);
      return next;
    });
  }

  function toggleSidebar() {
    if (isMobile) {
      setMobileOpen((v) => !v);
      return;
    }
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  }

  function onLogout() {
    clearSession();
    navigate("/login", { replace: true });
  }

  const desktopCollapsed = !isMobile && collapsed;
  const width = desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const drawerBody = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: desktopCollapsed ? "center" : "space-between",
          px: 1.5,
          py: 1.5,
          minHeight: 56,
        }}
      >
        {!desktopCollapsed ? (
          <Box sx={{ pl: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              PAY-GO Africa
            </Typography>
            {user ? (
              <Typography
                variant="caption"
                sx={{
                  color: isPlatformAdmin(user) ? "#fbbf24" : "#7dd3fc",
                  fontWeight: 600,
                  display: "block",
                  mt: 0.25,
                }}
              >
                {isPlatformAdmin(user) ? "Platform console" : "Company console"}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        <Tooltip title={isMobile ? "Close menu" : desktopCollapsed ? "Expand menu" : "Collapse menu"} placement="right">
          <IconButton onClick={toggleSidebar} size="small" sx={{ color: "#cbd5e1" }}>
            {isMobile || !desktopCollapsed ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: "rgba(148,163,184,0.2)" }} />

      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {visibleGroups.map((group) => {
          const open = isOpen(group.id);
          const hasActive = group.items.some((item) => pathMatches(location.pathname, item));
          const first = group.items[0];
          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              {desktopCollapsed ? (
                <Tooltip title={group.label} placement="right">
                  <ListItemButton
                    selected={hasActive}
                    onClick={() => {
                      if (first) navigate(first.to);
                      toggleGroup(group.id);
                    }}
                    sx={{ justifyContent: "center", px: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: "inherit", justifyContent: "center" }}>
                      {navIcon(first?.to ?? "/")}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <>
                  <ListItemButton onClick={() => toggleGroup(group.id)} sx={{ py: 0.75 }}>
                    <ListItemText
                      primary={group.label}
                      slotProps={{
                        primary: {
                          variant: "caption",
                          sx: {
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: hasActive ? "#fff" : "#94a3b8",
                          },
                        },
                      }}
                    />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding dense>
                      {group.items.map((item) => {
                        const selected = pathMatches(location.pathname, item);
                        return (
                          <ListItemButton
                            key={item.to}
                            component={NavLink}
                            to={item.to}
                            end={item.end}
                            selected={selected}
                            sx={{ pl: 2, py: 0.75 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>{navIcon(item.to)}</ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              slotProps={{
                                primary: {
                                  variant: "body2",
                                  sx: { fontWeight: selected ? 600 : 500 },
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: "rgba(148,163,184,0.2)" }} />
      <Box sx={{ p: desktopCollapsed ? 1 : 1.5 }}>
        {!desktopCollapsed && user ? (
          <Box sx={{ mb: 1, px: 0.5 }}>
            <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
              {userDisplayName(user)}
            </Typography>
            {userDisplayRole(user) ? (
              <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
                {userDisplayRole(user)}
              </Typography>
            ) : null}
            <Typography
              variant="caption"
              sx={{
                color: isPlatformAdmin(user) ? "#fbbf24" : "#7dd3fc",
                display: "block",
                mt: 0.5,
                fontWeight: 600,
              }}
            >
              {userScopeLabel(user)}
            </Typography>
          </Box>
        ) : null}
        <Tooltip title="Log out" placement="right">
          <ListItemButton onClick={onLogout} sx={{ justifyContent: desktopCollapsed ? "center" : "flex-start" }}>
            <ListItemIcon sx={{ minWidth: desktopCollapsed ? 0 : 36, color: "inherit", justifyContent: "center" }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {!desktopCollapsed ? <ListItemText primary="Log out" slotProps={{ primary: { variant: "body2" } }} /> : null}
          </ListItemButton>
        </Tooltip>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {isMobile ? (
        <>
          <AppBar
            position="fixed"
            color="secondary"
            elevation={0}
            sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
          >
            <Toolbar sx={{ minHeight: { xs: 56 }, gap: 1 }}>
              <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                PAY-GO Africa
              </Typography>
            </Toolbar>
          </AppBar>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {drawerBody}
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open
          sx={{
            width,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              transition: (t) =>
                t.transitions.create("width", {
                  easing: t.transitions.easing.sharp,
                  duration: t.transitions.duration.enteringScreen,
                }),
              overflowX: "hidden",
            },
          }}
        >
          {drawerBody}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          /* SOTA ladder: phone → large phone → tablet → laptop → desktop */
          px: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 4 },
          py: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
          width: isMobile ? "100%" : `calc(100% - ${width}px)`,
          minWidth: 0,
          pt: { xs: 9, sm: 9, md: 3, lg: 3.5 },
          maxWidth: { xl: 1600 },
          mx: { xl: "auto" },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
